import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../client';
import { useDraftMutation } from '../helpers/draftMutation';
import { applyDraftCache } from '../helpers/draftCacheHelpers';
import { appendMissingPositions } from '../helpers/placeholderStep';
import { reconcileEnvelope } from '../../draftGraph';
import { processLayoutKeys } from '../../query-keys';
import {
  type GetProcessLayoutResponse,
  type InsertStepTemplateRequest,
  type InsertStepTemplateResponse
} from '@bliks-io/contracts';
import { basePath } from './shared';

/**
 * Insert a Schrittvorlage into the draft. The response is authoritative — no
 * optimistic placeholder, the server mints the ids.
 */
export function useInsertStepTemplate(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      templateId,
      body
    }: {
      templateId: string;
      body: InsertStepTemplateRequest;
    }): Promise<InsertStepTemplateResponse> => {
      const { data } = await apiClient.post<InsertStepTemplateResponse>(
        `${basePath(processId)}/step-templates/${templateId}/insert`,
        body
      );
      return data;
    },
    onSuccess: (envelope) => {
      // Seed the layout cache FIRST: `reconcileEnvelope` upserts nodes only, so
      // without the positions the new nodes flash at the `{0,0}` sentinel until
      // the settle refetch lands.
      queryClient.setQueryData<GetProcessLayoutResponse>(
        processLayoutKeys.detail(processId, 'draft'),
        (prev) => (prev ? appendMissingPositions(prev, envelope.positions) : prev)
      );
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}
